import Errored from '../../core/Errored';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import ListResult from '../../core/ListResult';
import { globalStyles as styles } from '../../core/style/gstheme';
import TitledTextField from '../../core/TitledTextField';
import { debounce, makeCancelable, sortByLength } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import SchemeLink from '../core/SchemeLink';
import { MAX_LABEL_LENGTH } from '../constants';

class SuggestionField extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            isErrored: false,
            acOpen: false,
            checking: false,
            checked: false,
            isDuplicate: false,
            suggestedLabel: ""
        };
        this.cancellable = null;
    }

    findConcept = async value => {
        try {
            let searchParts = [
                {
                    predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                    value: "http://www.w3.org/2004/02/skos/core#Concept"
                },
                {
                    predicate: "http://www.w3.org/2004/02/skos/core#prefLabel",
                    value: value,
                    isExactMatch: false
                }
            ];
            const data = await this.doAutocomplete(
                {
                    graph: [this.props.graph],
                    search: searchParts,
                    language: this.props.lang,
                    entailment: this.props.optionForXL ? ["propertyChain"] : [],
                    predicates: [
                        "http://www.w3.org/2004/02/skos/core#prefLabel",
                        "http://www.w3.org/2004/02/skos/core#inScheme",
                        "https://grafsync.graphifi.com/schema/gs/iri",
                        "https://grafsync.graphifi.com/schema/gs/pendingDeletion"
                    ]
                }).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            const sortedResults = data.results && data.results.length > 0 ?
                sortByLength([...data.results], 'prefLabel', this.props.lang).slice(0, 5)
                :
                null;
            this.setState({
                results: sortedResults,
                acOpen: data.results && data.results.length > 0 ? true : false
            });
            // It's a duplicate if there is an equivalent label in the in-context scheme
            const isDuplicate = data.results && data.results.length > 0 ? data.results.some(result => {
                // We may get an edge case of mutiple prefLabels in a single language
                if (Array.isArray(result.prefLabel[this.props.lang])) {
                    return result.prefLabel[this.props.lang].some(label =>
                        label.toLowerCase() === this.state.label.toLowerCase()
                        && result.inScheme && result.inScheme.includes(this.props.schemeIRI));
                }
                return result.prefLabel[this.props.lang].toLowerCase() === this.state.suggestedLabel.toLowerCase()
                    && result.inScheme && result.inScheme.includes(this.props.schemeIRI);
            }) : false;
            this.setState({ isDeleted: false, isDuplicate: isDuplicate, checked: true, checking: false });
            this.props.isValid(this.state.suggestedLabel, isDuplicate);
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    };

    doAutocomplete(payload) {
        this.cancellable = makeCancelable(GS.autocomplete(payload));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    handleChange = event => {
        const t = event.target;
        this.setState({ [t.name]: t.value.trimLeft(), checked: false });
        if (t.value.trimLeft() !== "") {
            this.props.isValid(t.value.trim(), false);
            this.setState({ checking: true }, debounce(this.findConcept, this, [t.value.trimLeft(), this.props.lang]));
        }
        else {
            this.setState({ checking: false, checked: true, acOpen: false });
            this.props.isValid("", false);
        }
    };

    handleClickAway = () => this.setState({ acOpen: false });

    render() {

        const { schemeIRI, classes, lang, graph } = this.props;
        const { suggestedLabel, isDuplicate, acOpen, results, isErrored } = this.state;

        if (isErrored) return <Errored data={isErrored} />;

        return (
            <React.Fragment>
                <TitledTextField
                    dataTest="language-value"
                    name="suggestedLabel"
                    title={"Label"}
                    required={true}
                    text={suggestedLabel}
                    placeholder={"Enter suggested label"}
                    margin={true}
                    maxLength={MAX_LABEL_LENGTH}
                    lang={lang}
                    error={isDuplicate}
                    errorText={isDuplicate ?
                        "This value already exists on another concept in this language."
                        :
                        null
                    }
                    onChange={this.handleChange} />
                {acOpen && results && results.length > 0 &&
                    <ClickAwayListener onClickAway={this.handleClickAway}>
                        <List
                            data-test="list-results"
                            dense
                            className={classes.existingResults}
                            subheader={<ListSubheader className={classes.existingResultsHeader}>Existing Concepts</ListSubheader>}
                            style={{ marginTop: -8 }}>
                            {results.map((result, i) => {
                                let label = Array.isArray(result.prefLabel[lang]) ?
                                    result.prefLabel[lang].sort().join(" AND ")
                                    :
                                    result.prefLabel[lang];
                                let additionalLabel = null;
                                if (result.inScheme && !result.inScheme.includes(schemeIRI)) {
                                    additionalLabel = <div>
                                        {result.inScheme.map(scheme =>
                                            <SchemeLink key={scheme} id={scheme} graph={graph} defaultLang={lang} />
                                        )}
                                    </div>;
                                }
                                const currentIRI = result.iri && result.iri.length > 0 ?
                                    result.iri[result.iri.length - 1]
                                    :
                                    result.id;

                                return <ListResult
                                    key={i}
                                    text={label}
                                    iri={currentIRI}
                                    pendingDeletion={result.pendingDeletion}
                                    invalid={true}
                                    entailed={result.isEntailed}
                                    additionalLabel={additionalLabel}
                                />;
                            })}
                        </List>
                    </ClickAwayListener>
                }
            </React.Fragment>
        );
    }
}

SuggestionField.propTypes = {
    graph: PropTypes.string.isRequired,
    isValid: PropTypes.func.isRequired,
    schemeIRI: PropTypes.string,
    optionForXL: PropTypes.bool.isRequired,
    existingValues: PropTypes.object,
    name: PropTypes.string,
    lang: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(SuggestionField);